import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";

/**
 * TourOrderPdf — Phase 87ax (Feb 2026)
 *
 * Bon de tournée d'approvisionnement (A4 portrait) imprimé le matin avant
 * le départ du véhicule. Donne aux préparateurs Thai (qui ne lisent pas
 * forcément le français complexe) :
 *  - destination + slot ciblé en gros caractères
 *  - liste des produits à charger avec quantité totale en GRAS
 *  - détail des lots FIFO consommés (n° lot, DLC, qty) pour HACCP
 *  - case à cocher en fin de ligne (pointage manuel sur place)
 *  - signature préparateur + zone DLC dépassées (au retour, scanné dans AM)
 *
 * Pas de logo ni footer fancy — ce document est une fiche terrain pliable,
 * pas un courrier corporate. Lisibilité maximale.
 *
 * Props :
 *   - reference: "BTRN-260626-001"
 *   - date: "2026-06-26" (date de préparation)
 *   - operator: { name, email } | null
 *   - source: { name, type }
 *   - destination: { name, type, address?, city? }
 *   - emplacement: number | null   // E03, E12... null si CLIENT
 *   - lines: [{ product_name, total_qty, lots: [{lot_number, dlc, production_date, qty}] }]
 *   - reason?: string
 */

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
};

const fmtDateShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
};

const s = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 32,
    fontFamily: "Helvetica",
    color: "#0B0B0A",
    backgroundColor: "#FFFFFF",
    fontSize: 9.5,
  },

  // ===== Header =====
  header: {
    borderBottom: "2pt solid #1F4E3D",
    paddingBottom: 8,
    marginBottom: 14,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  brandBlock: { flexDirection: "column" },
  brand: { fontSize: 18, fontFamily: "Helvetica-Bold", letterSpacing: 0.4, color: "#1F4E3D" },
  brandSub: { fontSize: 8, color: "#575A57", letterSpacing: 1.2, textTransform: "uppercase", marginTop: 2 },
  refBlock: { alignItems: "flex-end" },
  refLabel: { fontSize: 7.5, color: "#A3A6A3", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 2 },
  refValue: { fontSize: 14, fontFamily: "Helvetica-Bold" },
  refDate: { fontSize: 9, color: "#575A57", marginTop: 2 },

  // ===== Title =====
  titleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  title: { fontSize: 19, fontFamily: "Helvetica-Bold", color: "#0B0B0A" },
  badge: {
    border: "1.2pt solid #1F4E3D",
    paddingHorizontal: 8,
    paddingVertical: 3,
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#1F4E3D",
  },

  // ===== Meta cards (source/dest/slot) =====
  metaGrid: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 14,
  },
  metaCard: {
    flex: 1,
    border: "1pt solid #C8C8C0",
    padding: 8,
    minHeight: 60,
  },
  metaCardEmphasis: {
    border: "1.5pt solid #1F4E3D",
    backgroundColor: "#F4F8F5",
  },
  metaLabel: { fontSize: 7.5, color: "#A3A6A3", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 4 },
  metaValue: { fontSize: 13, fontFamily: "Helvetica-Bold", lineHeight: 1.1 },
  metaSub: { fontSize: 8.5, color: "#575A57", marginTop: 3 },

  // ===== Lines table =====
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#1F4E3D",
    color: "#FFFFFF",
    paddingVertical: 5,
    paddingHorizontal: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 8.5,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  th: { color: "#FFFFFF" },
  thCheck: { width: 18, textAlign: "center" },
  thProduct: { flex: 3 },
  thQty: { width: 50, textAlign: "right" },
  thSlot: { width: 42, textAlign: "center" },
  thLots: { flex: 3, paddingLeft: 6 },

  productRow: {
    borderBottom: "1pt dashed #C8C8C0",
    paddingVertical: 5,
    paddingHorizontal: 6,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  tdCheck: { width: 18, alignItems: "center", justifyContent: "flex-start", paddingTop: 1 },
  checkbox: {
    width: 11,
    height: 11,
    border: "1.2pt solid #0B0B0A",
  },
  tdProduct: { flex: 3, paddingTop: 1 },
  productName: { fontSize: 11, fontFamily: "Helvetica-Bold", lineHeight: 1.15 },
  productSub: { fontSize: 8, color: "#A3A6A3", marginTop: 1 },
  tdQty: { width: 50, textAlign: "right", paddingTop: 1 },
  qtyNumber: { fontSize: 16, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  qtyUnit: { fontSize: 7.5, color: "#575A57" },
  tdSlot: { width: 42, alignItems: "center", justifyContent: "flex-start", paddingTop: 2 },
  slotBadge: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#1F4E3D",
    backgroundColor: "#F4F8F5",
    border: "1pt solid #1F4E3D",
    paddingHorizontal: 5,
    paddingVertical: 2,
    minWidth: 32,
    textAlign: "center",
  },
  tdLots: { flex: 3, paddingLeft: 6, paddingTop: 1 },

  lotLine: {
    flexDirection: "row",
    fontSize: 8.5,
    color: "#1A1C1A",
    marginBottom: 1,
  },
  lotNumber: { fontFamily: "Helvetica-Bold", width: 78 },
  lotDlc: { width: 80 },
  lotDlcUrgent: { color: "#8B1A1A", fontFamily: "Helvetica-Bold" },
  lotQty: { width: 40, textAlign: "right" },

  // ===== Total =====
  totalRow: {
    flexDirection: "row",
    backgroundColor: "#F4F4F0",
    paddingVertical: 7,
    paddingHorizontal: 6,
    marginTop: 2,
    borderBottom: "1.5pt solid #1F4E3D",
  },
  totalCheck: { width: 18 },
  totalLabel: { flex: 3, fontSize: 10, fontFamily: "Helvetica-Bold" },
  totalQty: { width: 50, textAlign: "right" },
  totalQtyNumber: { fontSize: 18, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  totalLots: { flex: 3, paddingLeft: 6, fontSize: 8.5, color: "#575A57" },

  // ===== Footer / signature =====
  footerBlock: { marginTop: 16, flexDirection: "row", gap: 12 },
  signCard: {
    flex: 1,
    border: "1pt solid #C8C8C0",
    padding: 8,
    minHeight: 80,
  },
  signLabel: { fontSize: 8, color: "#A3A6A3", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 18 },
  signLine: { borderBottom: "0.7pt solid #C8C8C0", marginTop: 22 },
  signNote: { fontSize: 7.5, color: "#A3A6A3", marginTop: 4, fontFamily: "Helvetica-Oblique" },

  // Footer page
  pageFooter: {
    position: "absolute",
    bottom: 14,
    left: 32,
    right: 32,
    fontSize: 7,
    color: "#A3A6A3",
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "0.5pt solid #E5E5E0",
    paddingTop: 4,
  },
});

// DLC dans 0-2 j → rouge
const isDlcUrgent = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = Math.round((d - now) / (1000 * 60 * 60 * 24));
  return diff <= 2;
};

export default function TourOrderPdf({
  reference,
  date,
  operator,
  source,
  destination,
  emplacement,
  lines,
  reason,
}) {
  const totalQty = lines.reduce((sum, l) => sum + (l.total_qty || 0), 0);
  const totalLots = lines.reduce((sum, l) => sum + (l.lots?.length || 0), 0);

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.header}>
          <View style={s.brandBlock}>
            <Text style={s.brand}>Maison Thaï</Text>
            <Text style={s.brandSub}>AgroManage · Bon de tournée</Text>
          </View>
          <View style={s.refBlock}>
            <Text style={s.refLabel}>Référence</Text>
            <Text style={s.refValue}>{reference}</Text>
            <Text style={s.refDate}>Préparé le {fmtDateLong(date)}</Text>
          </View>
        </View>

        {/* Title */}
        <View style={s.titleRow}>
          <Text style={s.title}>Préparation tournée d&apos;approvisionnement</Text>
          <Text style={s.badge}>FIFO STRICT</Text>
        </View>

        {/* Source / Destination */}
        <View style={s.metaGrid}>
          <View style={s.metaCard}>
            <Text style={s.metaLabel}>Départ (atelier)</Text>
            <Text style={s.metaValue}>{source?.name || "—"}</Text>
            {source?.type && <Text style={s.metaSub}>[{source.type}]</Text>}
          </View>
          <View style={[s.metaCard, s.metaCardEmphasis]}>
            <Text style={s.metaLabel}>Destination</Text>
            <Text style={s.metaValue}>{destination?.name || "—"}</Text>
            {destination?.type && (
              <Text style={s.metaSub}>
                [{destination.type}]
                {destination?.address ? ` · ${destination.address}` : ""}
                {destination?.city ? ` · ${destination.city}` : ""}
              </Text>
            )}
          </View>
        </View>

        {/* Table header — Phase 87az : colonne Slot par ligne */}
        <View style={s.tableHeader} fixed>
          <Text style={[s.th, s.thCheck]}>✓</Text>
          <Text style={[s.th, s.thProduct]}>Produit</Text>
          <Text style={[s.th, s.thQty]}>Qté</Text>
          <Text style={[s.th, s.thSlot]}>Slot</Text>
          <Text style={[s.th, s.thLots]}>Lots à charger (FIFO)</Text>
        </View>

        {/* Lines */}
        {lines.map((line, idx) => (
          <View key={idx} style={s.productRow} wrap={false}>
            <View style={s.tdCheck}>
              <View style={s.checkbox} />
            </View>
            <View style={s.tdProduct}>
              <Text style={s.productName}>{line.product_name}</Text>
              {line.product_sub && <Text style={s.productSub}>{line.product_sub}</Text>}
            </View>
            <View style={s.tdQty}>
              <Text style={s.qtyNumber}>{line.total_qty}</Text>
              <Text style={s.qtyUnit}>barq.</Text>
            </View>
            <View style={s.tdSlot}>
              <Text style={s.slotBadge}>{line.slot != null ? `E${line.slot}` : "—"}</Text>
            </View>
            <View style={s.tdLots}>
              {(line.lots || []).map((lot, lotIdx) => (
                <View key={lotIdx} style={s.lotLine}>
                  <Text style={s.lotNumber}>{lot.lot_number || "—"}</Text>
                  <Text style={[s.lotDlc, isDlcUrgent(lot.dlc) ? s.lotDlcUrgent : null]}>
                    DLC {fmtDateShort(lot.dlc)}
                  </Text>
                  <Text style={s.lotQty}>{lot.qty}×</Text>
                </View>
              ))}
            </View>
          </View>
        ))}

        {/* Total */}
        <View style={s.totalRow} wrap={false}>
          <View style={s.totalCheck} />
          <Text style={s.totalLabel}>TOTAL À CHARGER</Text>
          <View style={s.totalQty}>
            <Text style={s.totalQtyNumber}>{totalQty}</Text>
            <Text style={s.qtyUnit}>barquettes</Text>
          </View>
          <View style={s.tdSlot} />
          <Text style={s.totalLots}>
            {lines.length} produit{lines.length > 1 ? "s" : ""} · {totalLots} lot{totalLots > 1 ? "s" : ""}
          </Text>
        </View>

        {/* Signature blocks */}
        <View style={s.footerBlock} wrap={false}>
          <View style={s.signCard}>
            <Text style={s.signLabel}>Préparateur · Signature</Text>
            <Text style={{ fontSize: 9, color: "#575A57" }}>
              {operator?.name || operator?.email || "—"}
            </Text>
            <View style={s.signLine} />
            <Text style={s.signNote}>Confirme avoir chargé les quantités ci-dessus.</Text>
          </View>
          <View style={s.signCard}>
            <Text style={s.signLabel}>DLC dépassées au retour</Text>
            <Text style={s.signNote}>
              Lister les n° de lots récupérés en distributeur dont la DLC est dépassée — à scanner au retour
              dans AgroManage (mouvement WASTE).
            </Text>
            <View style={s.signLine} />
            <View style={[s.signLine, { marginTop: 14 }]} />
          </View>
        </View>

        {reason && (
          <View style={{ marginTop: 8 }}>
            <Text style={{ fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 1.2 }}>
              Motif
            </Text>
            <Text style={{ fontSize: 9, color: "#1A1C1A", marginTop: 2 }}>{reason}</Text>
          </View>
        )}

        {/* Page footer */}
        <View style={s.pageFooter} fixed>
          <Text>Maison Thaï SàRL · AgroManage</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
